import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import type { MeasurementRun } from '@traaker/shared';
import { audiencesApi } from '../../services/audiences';
import { formatDate } from '../../i18n/format';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTooltip, Legend);

const CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { intersect: false, mode: 'index' as const },
  plugins: {
    legend: { position: 'bottom' as const, labels: { usePointStyle: true, padding: 16, font: { size: 12 } } },
    tooltip: { backgroundColor: '#FFFFFF', titleColor: '#0B0B0F', bodyColor: '#0B0B0F', borderColor: '#E6E7EB', borderWidth: 1, cornerRadius: 8, padding: 12 },
  },
  scales: {
    y: { min: 0, max: 100, grid: { color: 'rgba(11,11,15,0.06)' }, ticks: { maxTicksLimit: 6 } },
    x: { grid: { display: false }, ticks: { maxTicksLimit: 8, autoSkip: true, maxRotation: 0 } },
  },
};

function scoreColor(score: number): string {
  return score >= 70 ? '#2e7d32' : score >= 40 ? '#ed6c02' : '#d32f2f';
}

function statusLabel(status: MeasurementRun['status']): { label: string; color: 'default' | 'info' | 'success' | 'warning' | 'error' } {
  switch (status) {
    case 'running':
      return { label: 'En cours', color: 'info' };
    case 'completed':
      return { label: 'Terminé', color: 'success' };
    case 'partial':
      return { label: 'Partiel', color: 'warning' };
    case 'failed':
      return { label: 'Échoué', color: 'error' };
    default:
      return { label: status, color: 'default' };
  }
}

/**
 * "Historique" tab (docs/features/retailer-catalog.md
 * §6.3): trend chart (score + % brand_covered) above a runs table. Selecting
 * a row calls `onSelectRun` so the parent can point the Synthèse/Réponses IA
 * tabs at that run instead of always the latest.
 */
export function MeasurementHistory({
  brandId,
  audienceId,
  activeRunId,
  onSelectRun,
}: {
  brandId: string;
  audienceId: string;
  activeRunId?: string;
  onSelectRun: (runId: string) => void;
}) {
  const [runs, setRuns] = useState<MeasurementRun[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    audiencesApi
      .listRuns(brandId, audienceId)
      .then((rows) => {
        if (!cancelled) setRuns(rows);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || 'Erreur de chargement');
      });
    return () => {
      cancelled = true;
    };
  }, [brandId, audienceId]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!runs) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (runs.length === 0) {
    return <Alert severity="info">Aucun historique encore. Lancez une analyse.</Alert>;
  }

  // Chart wants oldest → newest; the API returns newest-first.
  const chronological = [...runs].reverse();
  const scored = chronological.filter((r) => typeof (r as any).overallScore === 'number');
  const labels = chronological.map((r) => formatDate(r.startedAt, { day: '2-digit', month: 'short' }));
  const coveredPct = (r: MeasurementRun) => {
    const c = (r as any).coverageCounts as { covered: number; leaked: number; mentioned: number; none: number } | undefined;
    if (!c) return null;
    const total = c.covered + c.leaked + c.mentioned + c.none;
    return total ? Math.round((c.covered / total) * 100) : null;
  };

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Score GEO',
        data: chronological.map((r) => (r as any).overallScore ?? null),
        borderColor: '#2e7d32',
        borderWidth: 3,
        tension: 0.3,
        spanGaps: true,
        pointRadius: chronological.length <= 2 ? 4 : 0,
      },
      {
        label: '% Marque recommandée',
        data: chronological.map((r) => coveredPct(r)),
        borderColor: '#0288d1',
        borderDash: [5, 5],
        tension: 0.3,
        spanGaps: true,
        pointRadius: chronological.length <= 2 ? 4 : 0,
      },
    ],
  };

  return (
    <Box>
      {scored.length >= 2 && (
        <Box sx={{ height: 280, mb: 3 }}>
          <Line data={chartData} options={CHART_OPTIONS} />
        </Box>
      )}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Score</TableCell>
              <TableCell>Couverture</TableCell>
              <TableCell>Réponses</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.map((r) => {
              const status = statusLabel(r.status);
              const score = (r as any).overallScore as number | undefined;
              const cov = (r as any).coverageCounts as
                | { covered: number; leaked: number; mentioned: number; none: number }
                | undefined;
              const total = cov ? cov.covered + cov.leaked + cov.mentioned + cov.none : 0;
              return (
                <TableRow key={r.runId} selected={r.runId === activeRunId} hover>
                  <TableCell>{formatDate(r.startedAt, { day: '2-digit', month: 'short', year: 'numeric' })}</TableCell>
                  <TableCell>
                    {score != null ? (
                      <Chip size="small" label={score} sx={{ bgcolor: `${scoreColor(score)}20`, color: scoreColor(score), fontWeight: 700 }} />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell sx={{ minWidth: 120 }}>
                    {cov && total ? (
                      <Tooltip title={`${cov.covered} recommandé · ${cov.leaked} fuite · ${cov.mentioned} mentionné · ${cov.none} absent`}>
                        <Box sx={{ display: 'flex', width: '100%', borderRadius: 1, overflow: 'hidden', bgcolor: 'action.hover' }}>
                          {cov.covered > 0 && <Box sx={{ width: `${(cov.covered / total) * 100}%`, bgcolor: '#2e7d32', height: 6 }} />}
                          {cov.leaked > 0 && <Box sx={{ width: `${(cov.leaked / total) * 100}%`, bgcolor: '#ed6c02', height: 6 }} />}
                          {cov.mentioned > 0 && <Box sx={{ width: `${(cov.mentioned / total) * 100}%`, bgcolor: '#0288d1', height: 6 }} />}
                          {cov.none > 0 && <Box sx={{ width: `${(cov.none / total) * 100}%`, bgcolor: '#bdbdbd', height: 6 }} />}
                        </Box>
                      </Tooltip>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {r.completed}/{r.total}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={status.label} color={status.color} />
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      size="small"
                      variant={r.runId === activeRunId ? 'filled' : 'outlined'}
                      color={r.runId === activeRunId ? 'primary' : 'default'}
                      label="Voir"
                      onClick={() => onSelectRun(r.runId)}
                      clickable
                      disabled={r.status === 'running'}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
